import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { GLOSSARY } from "@/constants/testIds";
import { BookOpen } from "lucide-react";
import PageSections from "@/components/PageSections";
import { defaultGlossaryIntroSections } from "@/components/sectionTypes";
import TermCard from "@/components/TermCard";
import GlossarySearchBox from "@/components/GlossarySearchBox";
import GlossaryAlphaIndex from "@/components/GlossaryAlphaIndex";

export default function Glossary() {
  const [params, setParams] = useSearchParams();
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  // Which term the user has jumped to via the search dropdown. Kept in local
  // state (not URL) so hitting Enter in autocomplete doesn't leave `term_id`
  // stuck in the URL forever.
  const [jumpedId, setJumpedId] = useState(null);
  const urlFocusId = params.get("term_id");
  const focusId = jumpedId || urlFocusId;

  useEffect(() => { api.get("/glossary").then(r => setTerms(r.data)); }, []);

  // Scroll to the currently-focused term whenever it changes and after terms
  // finish loading.
  useEffect(() => {
    if (!focusId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`gl-${focusId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [focusId, terms]);

  const jumpTo = (term) => {
    if (!term) return;
    setJumpedId(term.term_id);
    // Keep the search text in the input as-is, but drop any lingering
    // `term_id` from the URL so refresh doesn't fight our local state.
    if (urlFocusId) {
      const next = new URLSearchParams(params);
      next.delete("term_id");
      setParams(next, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return terms;
    return terms.filter(t =>
      t.term.toLowerCase().includes(s) ||
      (t.definition || "").toLowerCase().includes(s) ||
      (t.definitions || []).some(d => (d || "").toLowerCase().includes(s)) ||
      (t.aliases || []).some(a => a.toLowerCase().includes(s)) ||
      (t.see_also || []).some(a => a.toLowerCase().includes(s))
    );
  }, [terms, q]);

  const groups = useMemo(() => {
    const g = {};
    for (const t of filtered) {
      const k = (t.term[0] || "#").toUpperCase();
      if (!g[k]) g[k] = [];
      g[k].push(t);
    }
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [filtered]);

  // Scroll-spy: track which letter section is currently near the top so the
  // alpha index can highlight it. Uses IntersectionObserver on the letter
  // header elements (`#gl-letter-X`).
  const [activeLetter, setActiveLetter] = useState(null);
  const headerRefs = useRef({});
  useEffect(() => {
    const nodes = Object.values(headerRefs.current).filter(Boolean);
    if (nodes.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Prefer the letter whose header is closest to the top-of-viewport.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveLetter(visible[0].target.dataset.letter);
        }
      },
      { rootMargin: "-88px 0px -70% 0px", threshold: 0 },
    );
    nodes.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [groups]);

  return (
    <div data-testid={GLOSSARY.page} className="max-w-6xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 dark:text-sky-400 mb-3 flex items-center gap-2">
        <BookOpen className="w-3.5 h-3.5"/> Reference
      </div>

      <PageSections pageKey="glossary_intro" defaults={defaultGlossaryIntroSections()}/>

      {/* Search box — sticky on scroll so it stays reachable while browsing. */}
      <div className="mt-8 sticky top-16 z-20 bg-white/90 dark:bg-slate-950/90 backdrop-blur py-3 -mx-6 px-6 md:-mx-10 md:px-10 border-b border-slate-100 dark:border-slate-800">
        <GlossarySearchBox
          terms={terms}
          value={q}
          onChange={setQ}
          onJumpTo={jumpTo}
        />
        {/* Compact horizontal alpha bar only on mobile — desktop uses the
            left rail below. */}
        <div className="mt-3 lg:hidden">
          <GlossaryAlphaIndex terms={filtered} focusLetter={activeLetter}/>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        {/* Desktop left alpha rail — mirrors the BlogReader TOC. */}
        <aside className="hidden lg:block lg:col-span-2">
          <div className="sticky top-40">
            <GlossaryAlphaIndex
              terms={filtered}
              focusLetter={activeLetter}
              orientation="vertical"
              label="Letters"
            />
          </div>
        </aside>

        <div className="lg:col-span-10 space-y-12">
          {groups.length === 0 && (
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-12 text-center text-slate-500">No terms.</div>
          )}
          {groups.map(([letter, list]) => (
            <section key={letter}>
              <div
                id={`gl-letter-${letter}`}
                ref={(el) => { headerRefs.current[letter] = el; }}
                data-letter={letter}
                data-testid={`glossary-letter-header-${letter}`}
                className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400 mb-4 pt-4 scroll-mt-40"
              >{letter}</div>
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {list.map(t => (
                  <div
                    key={t.term_id}
                    id={`gl-${t.term_id}`}
                    data-testid={GLOSSARY.item(t.term_id)}
                  >
                    <TermCard term={t} focused={focusId === t.term_id} testId={`glossary-card-${t.term_id}`}/>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
